// externals
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

// components
import TableGlobalAlertWarning from './TableGlobalAlertWarning';
import TableGlobalAlertError from './TableGlobalAlertError';
import TableGlobalAlertInfo from './TableGlobalAlertInfo';

// store
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as vehicleProtectionActionCreators from '@makemydeal/dr-dash-store/actions/vehicleProtectionActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import { DealTabs } from '@makemydeal/dr-shared-types';

//styles
import { AlertsAnimatedContainer, AlertsAnimatedContent, AlertsWrapper } from './TableGlobalAlerts.style';

// types
import { TableGlobalAlertNames } from '../../types';
import { tableGlobalAlertMessages } from './constants/tableGlobalAlertMessages';

const TableGlobalAlerts = () => {
    const dispatch = useDispatch();

    const isRatingFailed = useSelector(vehicleProtectionSelectors.isVppRatingFailed);
    const isRatingTimeout = useSelector(vehicleProtectionSelectors.isVppRatingTimeout);
    const isRatingNetworkFailure = useSelector(vehicleProtectionSelectors.hasVppRatingNetworkFailure);
    const isPaymentFailed = useSelector(vehicleProtectionSelectors.isPaymentFailed);
    const isFetchingRates = useSelector(vehicleProtectionSelectors.isFetchingVppRates);
    const isFetchingProducts = useSelector(vehicleProtectionSelectors.isFetchingVppProducts);
    const isGapCappedByLender = useSelector(vehicleProtectionSelectors.getIsGapCappedByLender);
    const hasProductsWithInapplicableRateAttribute = useSelector(
        vehicleProtectionSelectors.hasSelectedProductsWithInapplicableRateAttributes
    );
    const missingRequiredAttributes = useSelector(vehicleProtectionSelectors.hasMissingRequiredAttributes);
    const failedPaymentProductCodes = useSelector(vehicleProtectionSelectors.getFailedPaymentProductCodes);
    const products = useSelector(vehicleProtectionSelectors.getCatalogVppProducts);

    const isVppEnabled = useSelector(dealerSelectors.isVppEnabledForShopper);
    const isMenuPricingEnabled = useSelector(dealerSelectors.isMenuPricingEnabled);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const protectionEntityErrorCount = useSelector(dealScreenErrorSelectors.getEntityErrorCount(DealTabs.PROTECTION));
    const hasProtectionContractValidationError = isGCDynamicValidationsEnabled && protectionEntityErrorCount > 0;

    const theme = useInterstateTheme();

    const isFetchingFIE = isFetchingProducts || isFetchingRates;

    const retryRatesHandler = useCallback(() => {
        dispatch(vehicleProtectionActionCreators.retryFetchVehicleProtectionRates());
    }, [dispatch]);

    const alertComponents: JSX.Element[] = useMemo(() => {
        const components: JSX.Element[] = [];

        if (hasProtectionContractValidationError) {
            components.push(
                <TableGlobalAlertError
                    key={TableGlobalAlertNames.CONTRACT_VALIDATION_ERROR}
                    message={tableGlobalAlertMessages.contractValidationError}
                    name={TableGlobalAlertNames.CONTRACT_VALIDATION_ERROR}
                />
            );
        }

        if (isGapCappedByLender) {
            components.push(
                <TableGlobalAlertInfo
                    key={TableGlobalAlertNames.GAP_ADJUSTED_BY_LENDER}
                    message={tableGlobalAlertMessages.GapAdjustedByLender}
                    name={TableGlobalAlertNames.GAP_ADJUSTED_BY_LENDER}
                />
            );
        }

        if (isRatingFailed && !isRatingTimeout && !isRatingNetworkFailure) {
            components.push(
                <TableGlobalAlertError
                    key={TableGlobalAlertNames.RATING_FAILED}
                    message={tableGlobalAlertMessages.ratingFailed}
                    name={TableGlobalAlertNames.RATING_FAILED}
                />
            );
        }

        if (isRatingNetworkFailure) {
            components.push(
                <TableGlobalAlertError
                    key={TableGlobalAlertNames.RATING_NETWORK_FAILED}
                    message={tableGlobalAlertMessages.ratingNetworkFailed}
                    name={TableGlobalAlertNames.RATING_NETWORK_FAILED}
                    action={{ text: 'Try again.', handler: retryRatesHandler }}
                />
            );
        }

        if (isRatingTimeout) {
            components.push(
                <TableGlobalAlertError
                    key={TableGlobalAlertNames.RATING_TIMEOUT}
                    message={tableGlobalAlertMessages.ratingTimeout}
                    name={TableGlobalAlertNames.RATING_TIMEOUT}
                    action={{ text: 'Try again.', handler: retryRatesHandler }}
                />
            );
        }

        if (hasProductsWithInapplicableRateAttribute) {
            components.push(
                <TableGlobalAlertError
                    key={TableGlobalAlertNames.HAS_PRODUCTS_WITH_INAPPLICABLE_ATTRIBUTES}
                    message={tableGlobalAlertMessages.hasProductsWithInapplicableAttributes}
                    name={TableGlobalAlertNames.HAS_PRODUCTS_WITH_INAPPLICABLE_ATTRIBUTES}
                />
            );
        }

        if (isPaymentFailed) {
            if (failedPaymentProductCodes && failedPaymentProductCodes.length) {
                components.push(
                    <TableGlobalAlertError
                        key={TableGlobalAlertNames.UNSUPPORTED_PRODUCT}
                        message={tableGlobalAlertMessages.unsupportedPaymentProduct}
                        name={TableGlobalAlertNames.UNSUPPORTED_PRODUCT}
                    />
                );
            } else {
                components.push(
                    <TableGlobalAlertError
                        key={TableGlobalAlertNames.PAYMENT_FAILED}
                        message={tableGlobalAlertMessages.paymentFailed}
                        name={TableGlobalAlertNames.PAYMENT_FAILED}
                    />
                );
            }
        }

        if (!isVppEnabled) {
            components.push(
                <TableGlobalAlertWarning
                    key={TableGlobalAlertNames.VPP_DISABLED}
                    message={tableGlobalAlertMessages.vppDisabled}
                    name={TableGlobalAlertNames.VPP_DISABLED}
                />
            );
        } else if (!isMenuPricingEnabled) {
            components.push(
                <TableGlobalAlertWarning
                    key={TableGlobalAlertNames.MENU_PRICING_DISABLED}
                    message={tableGlobalAlertMessages.menuPricingDisabled}
                    name={TableGlobalAlertNames.MENU_PRICING_DISABLED}
                />
            );
        }

        if (missingRequiredAttributes) {
            components.push(
                <TableGlobalAlertWarning
                    key={TableGlobalAlertNames.MISSING_ATTRIBUTES}
                    message={tableGlobalAlertMessages.missingRequiredAttributes}
                    name={TableGlobalAlertNames.MISSING_ATTRIBUTES}
                />
            );
        }

        if (isFetchingFIE && products.length) {
            components.push(
                <TableGlobalAlertInfo
                    key={TableGlobalAlertNames.RATES_LOADING}
                    message={tableGlobalAlertMessages.productsLoading}
                    name={TableGlobalAlertNames.RATES_LOADING}
                />
            );
        }

        return components;
    }, [
        failedPaymentProductCodes,
        hasProtectionContractValidationError,
        isFetchingFIE,
        isMenuPricingEnabled,
        isPaymentFailed,
        isRatingFailed,
        isVppEnabled,
        missingRequiredAttributes,
        products.length,
        hasProductsWithInapplicableRateAttribute,
        isRatingTimeout,
        isRatingNetworkFailure,
        retryRatesHandler
    ]);

    const contentRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState(0);

    useEffect(() => {
        if (contentRef.current) {
            const clientRect = contentRef.current.getBoundingClientRect();
            setContentHeight(clientRect.height);
        }
    }, [alertComponents]);

    if (!alertComponents.length) {
        return null;
    }

    return (
        <AlertsWrapper theme={theme}>
            <AlertsAnimatedContainer height={contentHeight} theme={theme}>
                <AlertsAnimatedContent ref={contentRef}>{alertComponents}</AlertsAnimatedContent>
            </AlertsAnimatedContainer>
        </AlertsWrapper>
    );
};

export default TableGlobalAlerts;
